import { useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  Plus,
  Upload,
  Download,
  Trash2,
  CheckCircle2,
  Pencil,
  X,
  AlertTriangle,
  CalendarPlus,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuthStore } from '@/store/authStore';
import {
  getManifest,
  upsertEntry,
  deleteEntry,
  publishManifest,
  updateManifestHeader,
  bulkInsertEntries,
  carryForwardManifest,
  getCompanyAutocomplete,
  getDefaultColumnMapping,
  saveColumnMapping,
} from '@/services/db/pob';
import {
  type POBEntry,
  type POBEntryInput,
  type POBColumnMapping,
  type SSEContext,
  type POBEntryStatus,
  SSE_CONTEXT_LABELS,
  SSE_CONTEXT_BADGE,
} from '@/types/pob';
import POBColumnMapper from './POBColumnMapper';
import {
  applyMappingToRows,
  autoDetectMapping,
  hasRequiredMozaicColumns,
  identityMappingFromMozaicHeaders,
  type MOZAICField,
} from './pobImport';

const WRITE_ROLES = ['ssr', 'supervisor', 'tenant_admin', 'platform_admin'];

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-amber-100 text-amber-800',
  published: 'bg-mozaic-teal/15 text-mozaic-teal',
  archived: 'bg-muted text-muted-foreground',
};

export default function POBManifestPage() {
  const { manifestId } = useParams<{ manifestId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const userRole = useAuthStore((s) => s.userRole);
  const canWrite = !!userRole && WRITE_ROLES.includes(userRole);

  const manifestQ = useQuery({
    queryKey: ['pob_manifest', manifestId],
    queryFn: () => getManifest(manifestId!),
    enabled: !!manifestId,
  });

  const companiesQ = useQuery({
    queryKey: ['pob_companies', manifestQ.data?.project_id],
    queryFn: () => getCompanyAutocomplete(manifestQ.data!.project_id),
    enabled: !!manifestQ.data?.project_id,
  });

  const [entryPanelOpen, setEntryPanelOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<POBEntry | null>(null);
  const [importPanel, setImportPanel] = useState<{
    rows: POBEntryInput[];
    errors: string[];
    filename: string;
  } | null>(null);
  const [mapperState, setMapperState] = useState<{
    filename: string;
    fileColumns: string[];
    rawRows: Record<string, unknown>[];
    initialMap?: Record<string, MOZAICField>;
    savedMapping?: POBColumnMapping | null;
  } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const manifest = manifestQ.data;

  const sseSummary = useMemo(() => {
    const entries = manifest?.entries ?? [];
    const total = entries.length;
    const sse = entries.filter((e) => e.is_sse);
    const byCtx: Record<SSEContext | 'none', number> = {
      new_to_industry: 0,
      new_to_role: 0,
      new_to_company: 0,
      returned_from_leave: 0,
      none: 0,
    };
    sse.forEach((e) => {
      if (e.sse_context) byCtx[e.sse_context]++;
      else byCtx.none++;
    });
    return { total, sseCount: sse.length, byCtx };
  }, [manifest]);

  const ssePct =
    sseSummary.total > 0
      ? Math.round((sseSummary.sseCount / sseSummary.total) * 100)
      : 0;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['pob_manifest', manifestId] });
    qc.invalidateQueries({ queryKey: ['pob_manifests'] });
  };

  // Why (option C): explicit null/undefined/empty-string check guards against
  // PostgREST returning unexpected shapes and against DB rows where
  // sse_context was written as '' rather than NULL.
  const incompleteSSE = useMemo(
    () =>
      (manifest?.entries ?? []).filter(
        (e) =>
          e.is_sse === true &&
          (e.sse_context === null ||
            e.sse_context === undefined ||
            (e.sse_context as unknown) === '')
      ),
    [manifest]
  );

  const handleCarryForward = async () => {
    if (!manifest) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    const liveCount =
      manifest.entries?.filter((e) => !e.is_deleted && e.status !== 'departed').length ?? 0;
    const ok = window.confirm(
      `Copy ${liveCount} personnel to a new draft manifest for ${today}?`,
    );
    if (!ok) return;
    try {
      const { manifestId: newId } = await carryForwardManifest(
        manifest.id,
        today,
        manifest.project_id,
      );
      navigate(`/pob/${newId}`);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Carry forward failed.');
    }
  };

  const handlePublish = async () => {
    if (!manifestId) return;
    if (incompleteSSE.length > 0) {
      const ok = confirm(
        `${incompleteSSE.length} SSE ${
          incompleteSSE.length === 1 ? 'entry is' : 'entries are'
        } missing context details.\n\n` +
          `You can publish now and add context later, or cancel and complete them first.\n\n` +
          `OK = Publish Anyway\nCancel = I'll fix them`
      );
      if (!ok) return;
    }
    await publishManifest(manifestId);
    refresh();
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (!manifestId) return;
    if (!confirm('Remove this person from the manifest?')) return;
    await deleteEntry(entryId, manifestId);
    refresh();
  };

  const handleExportCSV = () => {
    if (!manifest) return;
    const rows = (manifest.entries ?? []).map((e) => ({
      first_name: e.first_name,
      last_name: e.last_name,
      company: e.company,
      position_title: e.position_title,
      hitch_on_date: e.hitch_on_date,
      hitch_off_date: e.hitch_off_date ?? '',
      status: e.status,
      is_sse: e.is_sse ? 'true' : 'false',
      sse_context: e.sse_context ?? '',
      sse_notes: e.sse_notes ?? '',
    }));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pob-${manifest.manifest_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFilePicked = (file: File) => {
    const reader = new FileReader();
    const isXlsx = file.name.toLowerCase().endsWith('.xlsx');
    reader.onload = async (ev) => {
      const result = ev.target?.result;
      if (!result) return;

      let rawRows: Record<string, unknown>[] = [];
      try {
        if (isXlsx) {
          const wb = XLSX.read(result, { type: 'binary' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          rawRows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true });
        } else {
          const parsed = Papa.parse<Record<string, unknown>>(result as string, {
            header: true,
            skipEmptyLines: true,
          });
          rawRows = parsed.data;
        }
      } catch (e) {
        setImportPanel({
          rows: [],
          errors: ['Failed to parse file: ' + (e instanceof Error ? e.message : '')],
          filename: file.name,
        });
        return;
      }

      if (rawRows.length === 0) {
        setImportPanel({ rows: [], errors: ['No rows found.'], filename: file.name });
        return;
      }

      const cols = Object.keys(rawRows[0]);

      // Fast path: MOZAIC template match — skip mapper, go to preview.
      if (hasRequiredMozaicColumns(cols)) {
        const identity = identityMappingFromMozaicHeaders(cols);
        const { rows, errors } = applyMappingToRows(rawRows, identity);
        setImportPanel({ rows, errors, filename: file.name });
        return;
      }

      // Try saved default mapping — show mapper pre-filled with banner.
      let savedMapping: POBColumnMapping | null = null;
      try {
        savedMapping = await getDefaultColumnMapping();
      } catch {
        savedMapping = null;
      }

      // Convert stored column_map into typed initialMap filtered to current file cols.
      let initialMap: Record<string, MOZAICField> | undefined;
      if (savedMapping) {
        const scoped: Record<string, MOZAICField> = {};
        for (const [col, field] of Object.entries(savedMapping.column_map)) {
          if (cols.includes(col)) scoped[col] = field as MOZAICField;
        }
        if (Object.keys(scoped).length > 0) initialMap = scoped;
      }
      if (!initialMap) initialMap = autoDetectMapping(cols);

      setMapperState({
        filename: file.name,
        fileColumns: cols,
        rawRows,
        initialMap,
        savedMapping,
      });
    };

    if (isXlsx) reader.readAsBinaryString(file);
    else reader.readAsText(file);
  };

  const handleMapperConfirm = async (params: {
    columnMap: Record<string, MOZAICField>;
    save: boolean;
    name: string;
    makeDefault: boolean;
  }) => {
    if (!mapperState) return;
    const { rawRows, filename } = mapperState;
    const { rows, errors } = applyMappingToRows(rawRows, params.columnMap);
    if (params.save) {
      try {
        await saveColumnMapping({
          name: params.name,
          columnMap: params.columnMap,
          makeDefault: params.makeDefault,
        });
      } catch (e) {
        errors.unshift(
          'Could not save mapping: ' + (e instanceof Error ? e.message : 'unknown')
        );
      }
    }
    setMapperState(null);
    setImportPanel({ rows, errors, filename });
  };

  const handleUseSavedMapping = () => {
    if (!mapperState?.savedMapping) return;
    const scoped: Record<string, MOZAICField> = {};
    for (const [col, field] of Object.entries(mapperState.savedMapping.column_map)) {
      if (mapperState.fileColumns.includes(col)) scoped[col] = field as MOZAICField;
    }
    const { rows, errors } = applyMappingToRows(mapperState.rawRows, scoped);
    setMapperState(null);
    setImportPanel({ rows, errors, filename: mapperState.filename });
  };

  const handleConfirmImport = async () => {
    if (!manifestId || !importPanel) return;
    const res = await bulkInsertEntries(manifestId, importPanel.rows);
    if (res.errors.length) {
      alert(
        `Imported ${res.inserted}. Errors:\n` +
          res.errors.map((e) => `• ${e.reason}`).join('\n')
      );
    } else {
      alert(`${res.inserted} people imported successfully`);
    }
    setImportPanel(null);
    refresh();
  };

  if (manifestQ.isLoading) {
    return <div className="p-8 text-sm text-muted-foreground">Loading...</div>;
  }
  if (!manifest) {
    return <div className="p-8 text-sm text-destructive">Manifest not found</div>;
  }

  const isEditable = canWrite && manifest.status !== 'archived';

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              POB — {format(new Date(manifest.manifest_date + 'T00:00:00'), 'MMM d, yyyy')}
            </h1>
            <Badge className={`${STATUS_BADGE[manifest.status]} capitalize`}>
              {manifest.status}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {manifest.facility_name ?? manifest.projects?.name ?? ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/pob')}>
            Back
          </Button>
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="mr-1 h-4 w-4" />
            Export CSV
          </Button>
          {isEditable && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFilePicked(f);
                  e.target.value = '';
                }}
              />
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="mr-1 h-4 w-4" />
                Import CSV/Excel
              </Button>
              {manifest.status === 'draft' && (
                <Button
                  onClick={handlePublish}
                  className="bg-mozaic-teal hover:bg-mozaic-teal/90"
                >
                  <CheckCircle2 className="mr-1 h-4 w-4" />
                  Publish Manifest
                </Button>
              )}
              {manifest.status === 'published' &&
                manifest.manifest_date < format(new Date(), 'yyyy-MM-dd') && (
                  <Button variant="outline" onClick={handleCarryForward}>
                    <CalendarPlus className="mr-1 h-4 w-4" />
                    Carry Forward to Today
                  </Button>
                )}
            </>
          )}
        </div>
      </header>

      {isEditable && manifest.status === 'draft' && (
        <ManifestHeaderEditor manifest={manifest} onSaved={refresh} />
      )}

      {incompleteSSE.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-medium">
              {incompleteSSE.length} SSE{' '}
              {incompleteSSE.length === 1 ? 'entry needs' : 'entries need'}{' '}
              context.
            </p>
            <p className="text-xs">
              Click each flagged person to add SSE details. You can still
              publish with missing context, but we'll warn you.
            </p>
          </div>
        </div>
      )}

      <Card className="mb-4">
        <CardContent className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4">
          <SummaryStat label="Total POB" value={sseSummary.total} />
          <SummaryStat
            label="SSEs"
            value={`${sseSummary.sseCount} (${ssePct}%)`}
          />
          <div className="col-span-2 text-xs text-muted-foreground">
            {[
              [sseSummary.byCtx.new_to_industry, 'new to industry'],
              [sseSummary.byCtx.new_to_role, 'new to role'],
              [sseSummary.byCtx.new_to_company, 'new company'],
              [sseSummary.byCtx.returned_from_leave, 'from leave'],
              [sseSummary.byCtx.none, 'uncontexted'],
            ]
              .filter(([n]) => (n as number) > 0)
              .map(([n, label]) => `${n} ${label}`)
              .join(' · ') || 'No SSEs'}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Hitch On</TableHead>
                <TableHead>Hitch Off</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>SSE</TableHead>
                {isEditable && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(manifest.entries ?? []).length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={isEditable ? 8 : 7}
                    className="text-center text-sm text-muted-foreground"
                  >
                    No personnel on this manifest yet.
                  </TableCell>
                </TableRow>
              ) : (
                (manifest.entries ?? []).map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">
                      {e.last_name}, {e.first_name}
                    </TableCell>
                    <TableCell>{e.company}</TableCell>
                    <TableCell>{e.position_title}</TableCell>
                    <TableCell>
                      {format(new Date(e.hitch_on_date + 'T00:00:00'), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      {e.hitch_off_date
                        ? format(new Date(e.hitch_off_date + 'T00:00:00'), 'MMM d, yyyy')
                        : '—'}
                    </TableCell>
                    <TableCell className="capitalize">
                      {e.status.replace('_', ' ')}
                    </TableCell>
                    <TableCell>
                      {e.is_sse ? (
                        e.sse_context ? (
                          <Badge
                            className={`border ${SSE_CONTEXT_BADGE[e.sse_context]}`}
                            variant="outline"
                          >
                            {SSE_CONTEXT_LABELS[e.sse_context].toUpperCase()}
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="border border-amber-400 bg-amber-100 text-amber-800"
                            title="SSE context required — click Edit to complete"
                          >
                            <AlertTriangle className="mr-1 h-3 w-3" />
                            SSE ⚠
                          </Badge>
                        )
                      ) : null}
                    </TableCell>
                    {isEditable && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => {
                              setEditingEntry(e);
                              setEntryPanelOpen(true);
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEntry(e.id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isEditable && (
        <div className="mt-4 flex justify-start">
          <Button
            onClick={() => {
              setEditingEntry(null);
              setEntryPanelOpen(true);
            }}
            className="bg-mozaic-teal hover:bg-mozaic-teal/90"
          >
            <Plus className="mr-1 h-4 w-4" />
            Add Person
          </Button>
        </div>
      )}

      <Sheet open={entryPanelOpen} onOpenChange={setEntryPanelOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {editingEntry ? 'Edit Person' : 'Add Person'}
            </SheetTitle>
          </SheetHeader>
          <EntryForm
            manifestId={manifest.id}
            manifestDate={manifest.manifest_date}
            initial={editingEntry}
            companies={companiesQ.data ?? []}
            onSaved={() => {
              setEntryPanelOpen(false);
              setEditingEntry(null);
              refresh();
            }}
            onCancel={() => {
              setEntryPanelOpen(false);
              setEditingEntry(null);
            }}
          />
        </SheetContent>
      </Sheet>

      <Sheet
        open={!!mapperState}
        onOpenChange={(o) => !o && setMapperState(null)}
      >
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Import Setup</SheetTitle>
          </SheetHeader>
          {mapperState && (
            <>
              {mapperState.savedMapping && (
                <div className="mt-4 rounded-md border border-mozaic-teal/30 bg-mozaic-teal/5 p-3 text-sm">
                  <p>
                    We found a saved mapping:{' '}
                    <strong>{mapperState.savedMapping.mapping_name}</strong>
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      className="bg-mozaic-teal hover:bg-mozaic-teal/90"
                      onClick={handleUseSavedMapping}
                    >
                      Use saved mapping
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setMapperState((s) =>
                          s ? { ...s, savedMapping: null } : s
                        )
                      }
                    >
                      Map manually
                    </Button>
                  </div>
                </div>
              )}
              <POBColumnMapper
                filename={mapperState.filename}
                fileColumns={mapperState.fileColumns}
                initialMap={mapperState.initialMap}
                onCancel={() => setMapperState(null)}
                onConfirm={handleMapperConfirm}
              />
            </>
          )}
        </SheetContent>
      </Sheet>

      <Sheet
        open={!!importPanel}
        onOpenChange={(o) => !o && setImportPanel(null)}
      >
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Import Preview</SheetTitle>
          </SheetHeader>
          {importPanel && (
            <div className="mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                {importPanel.filename}
              </p>
              {importPanel.errors.length > 0 && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                  <p className="font-medium">
                    {importPanel.rows.length > 0
                      ? `${importPanel.errors.length} row(s) skipped:`
                      : 'Cannot import:'}
                  </p>
                  <ul className="mt-1 list-disc pl-5">
                    {importPanel.errors.slice(0, 10).map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
              {importPanel.rows.length > 0 && (
                <>
                  <p className="text-sm">
                    Found <strong>{importPanel.rows.length}</strong> rows.
                    Preview:
                  </p>
                  <div className="max-h-64 overflow-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Position</TableHead>
                          <TableHead>Hitch On</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importPanel.rows.slice(0, 5).map((r, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              {r.last_name}, {r.first_name}
                            </TableCell>
                            <TableCell>{r.company}</TableCell>
                            <TableCell>{r.position_title}</TableCell>
                            <TableCell>{r.hitch_on_date}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setImportPanel(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleConfirmImport}
                      className="bg-mozaic-teal hover:bg-mozaic-teal/90"
                    >
                      Import {importPanel.rows.length} people
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
    </div>
  );
}

function ManifestHeaderEditor({
  manifest,
  onSaved,
}: {
  manifest: { id: string; manifest_date: string; facility_name: string | null };
  onSaved: () => void;
}) {
  const [date, setDate] = useState(manifest.manifest_date);
  const [facility, setFacility] = useState(manifest.facility_name ?? '');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await updateManifestHeader(manifest.id, {
      manifest_date: date,
      facility_name: facility || null,
    });
    setSaving(false);
    setDirty(false);
    onSaved();
  };

  return (
    <Card className="mb-4">
      <CardContent className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3">
        <div>
          <Label className="text-xs">Date</Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setDirty(true);
            }}
          />
        </div>
        <div>
          <Label className="text-xs">Facility</Label>
          <Input
            value={facility}
            onChange={(e) => {
              setFacility(e.target.value);
              setDirty(true);
            }}
          />
        </div>
        <div className="flex items-end">
          <Button onClick={save} disabled={!dirty || saving} className="w-full">
            {saving ? 'Saving...' : 'Save Header'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EntryForm({
  manifestId,
  initial,
  companies,
  onSaved,
  onCancel,
}: {
  manifestId: string;
  manifestDate: string;
  initial: POBEntry | null;
  companies: string[];
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [firstName, setFirstName] = useState(initial?.first_name ?? '');
  const [lastName, setLastName] = useState(initial?.last_name ?? '');
  const [company, setCompany] = useState(initial?.company ?? '');
  const [position, setPosition] = useState(initial?.position_title ?? '');
  const [hitchOn, setHitchOn] = useState(
    initial?.hitch_on_date ?? format(new Date(), 'yyyy-MM-dd')
  );
  const [hitchOff, setHitchOff] = useState(initial?.hitch_off_date ?? '');
  const [status, setStatus] = useState<POBEntryStatus>(
    initial?.status ?? 'on_board'
  );
  const [isSSE, setIsSSE] = useState<boolean>(initial?.is_sse ?? false);
  const [sseContext, setSseContext] = useState<SSEContext | ''>(
    initial?.sse_context ?? ''
  );
  const [sseNotes, setSseNotes] = useState(initial?.sse_notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setError(null);
    if (!firstName || !lastName || !company || !position || !hitchOn) {
      setError('All starred fields are required');
      return;
    }
    if (hitchOff && hitchOff < hitchOn) {
      setError('Hitch off date must be after hitch on date');
      return;
    }
    // Why: SSE context is encouraged but not required to save. Bulk
    // imports may land without context; users complete it after import.
    // The manifest page shows an amber banner + "SSE ⚠" badge and the
    // Publish button warns. DB CHECK dropped in migration 20260414000003.
    setSaving(true);
    try {
      await upsertEntry(manifestId, {
        id: initial?.id,
        first_name: firstName,
        last_name: lastName,
        company,
        position_title: position,
        hitch_on_date: hitchOn,
        hitch_off_date: hitchOff || null,
        status,
        is_sse: isSSE,
        // Why: never persist an empty string — column-level CHECK on
        // sse_context requires NULL or a valid enum value. A blank
        // selection means "incomplete" and must be NULL so the UI
        // flags it via the amber SSE⚠ badge.
        sse_context: isSSE && sseContext ? (sseContext as SSEContext) : null,
        sse_notes: isSSE ? sseNotes || null : null,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>First Name *</Label>
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div>
          <Label>Last Name *</Label>
          <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>

      <div>
        <Label>Company *</Label>
        <Input
          list="pob-company-list"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="e.g. Acme Services"
        />
        <datalist id="pob-company-list">
          {companies.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
      </div>

      <div>
        <Label>Position Title *</Label>
        <Input value={position} onChange={(e) => setPosition(e.target.value)} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Hitch On *</Label>
          <Input
            type="date"
            value={hitchOn}
            onChange={(e) => setHitchOn(e.target.value)}
          />
        </div>
        <div>
          <Label>Hitch Off</Label>
          <Input
            type="date"
            value={hitchOff}
            onChange={(e) => setHitchOff(e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label>Status *</Label>
        <Select
          value={status}
          onValueChange={(v) => setStatus(v as POBEntryStatus)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="on_board">On Board</SelectItem>
            <SelectItem value="planned">Planned</SelectItem>
            <SelectItem value="departed">Departed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border border-input p-3">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={isSSE}
            onChange={(e) => {
              setIsSSE(e.target.checked);
              if (!e.target.checked) {
                setSseContext('');
                setSseNotes('');
              }
            }}
          />
          <span>
            <span className="font-medium">Short Service Employee (SSE)</span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              Check if this person has less than 6 months experience in their
              current craft or position.
            </span>
          </span>
        </label>
      </div>

      {isSSE && (
        <div
          className={`rounded-md border p-3 space-y-3 ${
            sseContext
              ? 'border-amber-300 bg-amber-50'
              : 'border-amber-500 bg-amber-100 ring-1 ring-amber-400'
          }`}
        >
          {!sseContext && (
            <p className="text-xs font-semibold text-amber-900">
              SSE context — required to publish without a warning.
            </p>
          )}
          <div>
            <Label>SSE Context</Label>
            <div className="mt-1 space-y-1 text-sm">
              {(
                [
                  ['new_to_industry', 'New to industry'],
                  ['new_to_role', 'New to role (experienced, new position)'],
                  ['new_to_company', 'New to company (same role, new employer)'],
                  ['returned_from_leave', 'Returning from leave'],
                ] as [SSEContext, string][]
              ).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="sse_context"
                    checked={sseContext === value}
                    onChange={() => setSseContext(value)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>SSE Notes</Label>
            <Textarea
              rows={3}
              value={sseNotes}
              onChange={(e) => setSseNotes(e.target.value)}
              placeholder="Add context for the supervisor (e.g. '10 years offshore, first time as D1')"
            />
          </div>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>
          <X className="mr-1 h-4 w-4" />
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-mozaic-teal hover:bg-mozaic-teal/90"
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
